import { z } from 'zod';

export const RATE_MODE_OPTIONS = [
  { value: 'fixed', label: 'Tasa fija' },
  { value: 'stepped', label: 'Tasa escalonada' },
] as const;

// Mirrors the backend's shared RateTier bounds (src/rate_schedule.py /
// src/v2/common/rate_schedule.py): lower-bounded at -1 (exclusive) since
// (1 + annual_rate) ** (1/12) needs a positive base, upper-bounded at the
// 0.05 (5%) product ceiling. Unlike `projection_rate` (the fixed field,
// always positive), a tier may be negative to model a bad-year rentability
// vector — but only inside a genuine multi-tier schedule; see the
// single-tier check below.
const RATE_TIER_MESSAGE = 'La tasa de proyección debe ser mayor a -100% y menor o igual a 5%. Por favor, ajústala.';
const FLAT_SCHEDULE_MESSAGE = 'Un tramo único representa una tasa fija y debe ser positiva. Por favor, ajústala.';

const rateTierSchema = z.object({
  annual_rate: z.coerce.number().gt(-1, RATE_TIER_MESSAGE).lte(0.05, RATE_TIER_MESSAGE),
  // YYYY-MM, null only allowed on the last tier (validated below).
  until: z
    .string()
    .regex(/^\d{4}-\d{2}$/, 'Formato YYYY-MM')
    .nullable(),
});

export type RateTierFormValue = z.infer<typeof rateTierSchema>;

export const projectionRateScheduleSchema = z
  .array(rateTierSchema)
  .min(1, 'Debe haber al menos un tramo')
  .superRefine((tiers, ctx) => {
    if (tiers.length === 1 && tiers[0].annual_rate <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: FLAT_SCHEDULE_MESSAGE,
        path: [],
      });
    }
    tiers.forEach((tier, i) => {
      const isLast = i === tiers.length - 1;
      if (!isLast && tier.until === null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Sólo el último tramo puede no tener fecha de fin',
          path: [i, 'until'],
        });
      }
    });
    const bounded = tiers
      .filter((t) => t.until !== null)
      .map((t) => t.until as string);
    for (let i = 1; i < bounded.length; i += 1) {
      if (bounded[i] <= bounded[i - 1]) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Las fechas deben ser estrictamente ascendentes',
          path: [i, 'until'],
        });
      }
    }
  });

/**
 * Shared rate fields any savings/APV product mixes into its schema to support
 * the fixed-vs-stepped projection rate toggle. `rate_mode` is a UI-only toggle
 * the backend does not understand — the service layer strips it and forwards
 * exactly one of `projection_rate` / `projection_rate_schedule`.
 */
export const rateModeFields = {
  rate_mode: z.enum(['fixed', 'stepped']),
  projection_rate: z.coerce.number().optional(),
  projection_rate_schedule: projectionRateScheduleSchema.optional(),
};

/**
 * Cross-field validation shared by every product with a rate toggle. Ensures
 * the field required by the active `rate_mode` is present. Call from the host
 * schema's own `superRefine`.
 */
export function refineRateMode(
  values: { rate_mode: 'fixed' | 'stepped'; projection_rate?: number; projection_rate_schedule?: RateTierFormValue[] },
  ctx: z.RefinementCtx,
) {
  if (values.rate_mode === 'fixed') {
    if (values.projection_rate === undefined || Number.isNaN(values.projection_rate)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Requerido en modo tasa fija',
        path: ['projection_rate'],
      });
    }
  } else if (!values.projection_rate_schedule || values.projection_rate_schedule.length === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Debe definir al menos un tramo',
      path: ['projection_rate_schedule'],
    });
  }
}

export const rateModeDefaults = {
  rate_mode: 'fixed' as const,
  projection_rate_schedule: [
    { annual_rate: 0.021, until: '2027-12' },
    { annual_rate: 0.015, until: null },
  ] as RateTierFormValue[],
};

/**
 * Serialize the rate fields for the backend. Strips the `rate_mode` UI toggle
 * and forwards exactly one field: the fixed rate, or the schedule JSON-encoded
 * (nested objects must travel as a single query param, same trick as USI
 * `beneficiaries`; the backend `_parse_projection_rate_schedule` decodes it).
 */
export function buildRatePayload(values: {
  rate_mode: 'fixed' | 'stepped';
  projection_rate?: number;
  projection_rate_schedule?: RateTierFormValue[];
}) {
  return values.rate_mode === 'fixed'
    ? { projection_rate: values.projection_rate }
    : { projection_rate_schedule: JSON.stringify(values.projection_rate_schedule ?? []) };
}
